'use client';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useRef, Children, useMemo, cloneElement, isValidElement, useEffect, useContext } from 'react';
import d from 'react-slick';
import t from 'tiny-warning';
import { removeCoreProps } from '../core/core.utils.js';
import { useComponentStylesLoaded } from '../../hooks/use-component-styles-loaded.js';
import { useDeprecatedProps } from '../../hooks/use-deprecated-props.js';
import { usePreventVerticalScroll } from '../../hooks/use-prevent-vertical-scroll.js';
import { usePromoBarDarkMode } from './carousel.hooks.js';
import { processStyleProps } from '../../../core/utilities/swan-style.js';
import { ScreenClassContext } from '../../contexts/screen-class-provider/index.js';
import { Button } from '../button/button.component.js';
import { Icon } from '../icon/icon.component.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { SWAN_STYLE_KEY_MAP } from '../head/stylesheets.component.js';
import { useId } from '../../hooks/use-id.js';
import { assignRefs } from '../../../core/utilities/ref.utils.js';
import { className } from '../../../core/utilities/classnames.utils.js';
import { tokensRaw } from '../../../core/tokens/tokens-raw.js';
import { isNotProduction } from '../../../core/utilities/env.utils.js';
import { VisuallyHidden } from '../visually-hidden/visually-hidden.component.js';

function _define_property(e, r, o) {
    return r in e ? Object.defineProperty(e, r, {
        value: o,
        enumerable: true,
        configurable: true,
        writable: true
    }) : e[r] = o, e;
}
function _object_spread(e) {
    for(var r = 1; r < arguments.length; r++){
        var o = null != arguments[r] ? arguments[r] : {}, t = Object.keys(o);
        "function" == typeof Object.getOwnPropertySymbols && (t = t.concat(Object.getOwnPropertySymbols(o).filter(function(e) {
            return Object.getOwnPropertyDescriptor(o, e).enumerable;
        }))), t.forEach(function(r) {
            _define_property(e, r, o[r]);
        });
    }
    return e;
}
function ownKeys(e, r) {
    var o = Object.keys(e);
    if (Object.getOwnPropertySymbols) {
        var t = Object.getOwnPropertySymbols(e);
        o.push.apply(o, t);
    }
    return o;
}
function _object_spread_props(e, r) {
    return r = null != r ? r : {}, Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(r)) : ownKeys(Object(r)).forEach(function(o) {
        Object.defineProperty(e, o, Object.getOwnPropertyDescriptor(r, o));
    }), e;
}
function _object_without_properties(e, r) {
    if (null == e) return {};
    var o, t, s = _object_without_properties_loose(e, r);
    if (Object.getOwnPropertySymbols) {
        var a = Object.getOwnPropertySymbols(e);
        for(t = 0; t < a.length; t++)o = a[t], !(r.indexOf(o) >= 0) && Object.prototype.propertyIsEnumerable.call(e, o) && (s[o] = e[o]);
    }
    return s;
}
function _object_without_properties_loose(e, r) {
    if (null == e) return {};
    var o, t, s = {}, a = Object.keys(e);
    for(t = 0; t < a.length; t++)o = a[t], r.indexOf(o) >= 0 || (s[o] = e[o]);
    return s;
}
let CarouselButton = ({ onClick: o, accessibleText: t, iconType: s, className: a, style: i })=>{
    let l = useId();
    return jsxs(Button, {
        buttonShape: "round",
        onClick: o,
        disabled: null == a ? void 0 : a.includes('slick-disabled'),
        className: a,
        style: i,
        "aria-labelledby": l,
        children: [
            jsx(VisuallyHidden, {
                id: l,
                children: t
            }),
            jsx(Icon, {
                alt: "",
                iconType: s
            })
        ]
    });
}, useSlidesToShow = (e = 1)=>{
    let r = useContext(ScreenClassContext);
    if ('number' == typeof e) return e;
    let o = 1;
    if (void 0 === r) {
        if (isNotProduction()) throw Error("A responsive value for `slidesToShow` may only be used inside of a ScreenClassProvider. Make sure that you've rendered a ScreenClassProvider above this component your tree.");
        return o;
    }
    for (let s of [
        'xs',
        'sm',
        'md',
        'lg',
        'xl'
    ]){
        var t;
        if (o = null !== (t = e[s]) && void 0 !== t ? t : o, r === s) break;
    }
    return o;
};
const UninitializedCarousel = (r)=>{
    let { children: t, className: s } = r;
    return jsx("div", {
        className: `swan-carousel swan-carousel-placeholder ${s}`,
        children: jsx("div", {
            className: "slick-list",
            children: jsx("div", {
                className: "slick-track",
                children: Children.map(t, (r)=>jsx("div", {
                        className: "slick-slide",
                        children: jsx("div", {
                            children: r
                        })
                    }))
            })
        })
    });
};
const Carousel = renderWithRef('Carousel', null, (a, m)=>{
    useComponentStylesLoaded('Carousel', SWAN_STYLE_KEY_MAP.carousel);
    let { as: y, component: v, skin: j = 'standard', progressIndicator: _ = 'none', progressIndicatorAlignment: S = false, peek: C = false, gridGutters: N = false, gridGuttersVariant: A = 'standard', arrowVerticalPosition: D, arrowVerticalOffset: B = '0px', children: E, dots: F = false, arrows: G = true, variableWidth: M = false, centerMode: V = false, slidesToShow: I = 1, centerPadding: U, role: $ = 'region', 'aria-label': z, 'aria-labelledby': K, 'aria-roledescription': R, accessibleTextForPrevious: W, accessibleTextForNext: X, accessibleTextForDots: L, accessibleTextForVisibleSlides: q, accessibleTextForSlide: H, customPaging: J, currentSlide: Q, initialSlide: Y = Q, id: Z, fade: ee, darkMode: er } = a, eo = _object_without_properties(a, [
        "as",
        "component",
        "skin",
        "progressIndicator",
        "progressIndicatorAlignment",
        "peek",
        "gridGutters",
        "gridGuttersVariant",
        "arrowVerticalPosition",
        "arrowVerticalOffset",
        "children",
        "dots",
        "arrows",
        "variableWidth",
        "centerMode",
        "slidesToShow",
        "centerPadding",
        "role",
        'aria-label',
        'aria-labelledby',
        'aria-roledescription',
        "accessibleTextForPrevious",
        "accessibleTextForNext",
        "accessibleTextForDots",
        "accessibleTextForVisibleSlides",
        "accessibleTextForSlide",
        "customPaging",
        "currentSlide",
        "initialSlide",
        "id",
        "fade",
        "darkMode"
    ]);
    useDeprecatedProps('Carousel', a, [
        {
            propName: 'gridGuttersVariant',
            instructions: 'The prop has been deprecated, as it is rarely used and to maintain consistency with GridContainer while reducing UX debt.'
        }
    ]);
    let [et, es] = useState('promo-bar' === j), ea = useId(Z), ei = `swan-carousel-placeholder-${ea}`, el = useRef(null), en = useRef(void 0), ec = assignRefs(m, en), ed = F || 'none' !== _ || !!J, { forceDarkMode: ep, beforeChange: eu } = usePromoBarDarkMode(en, a), eb = useSlidesToShow(I), em = {
        prevArrow: jsx(CarouselButton, {
            accessibleText: W,
            iconType: "chevronLeft"
        }),
        nextArrow: jsx(CarouselButton, {
            accessibleText: X,
            iconType: "chevronRight"
        }),
        arrows: G,
        centerMode: !!(V || C),
        dots: ed,
        customPaging: ed ? J || ((r)=>jsx("button", {
                children: Array.isArray(L) && void 0 != L[r] ? L[r] : r + 1
            })) : void 0,
        appendDots: ed ? (r)=>jsx("ul", {
                children: Children.map(r, (e)=>isValidElement(e) ? cloneElement(e, {}, cloneElement(e.props.children, {
                        'aria-current': 'slick-active' === e.props.className
                    })) : e)
            }) : void 0,
        initialSlide: 'number' == typeof Y ? Y : C && eb > 1 ? 1 : 0,
        slidesToShow: eb,
        variableWidth: M,
        autoplay: false,
        fade: null != ee ? ee : 'promo-bar' === j
    }, ef = {
        arrows: G
    };
    void 0 !== U && (em.centerPadding = U, ef.centerPadding = U), C && (ef.centerPadding = '8'), t(ed || G, 'At least one of dots or arrow must be enabled for a carousel'), t(void 0 === y && void 0 === v, 'Carousel does not support the `component` or `as` prop because it must render a third-party component');
    let eh = processStyleProps(_object_spread_props(_object_spread({}, eo), {
        darkMode: ep || er
    })), ew = removeCoreProps(eo), ey = className(eo.className, ...eh, 'swan-carousel', {
        [`swan-carousel-skin-${j}`]: 'standard' !== j,
        'swan-carousel-dots': em.dots,
        'swan-carousel-dots-left': em.dots && 'left' === S,
        'swan-carousel-dots-inset': 'dots-inset' === _,
        'swan-carousel-hide-arrows': !em.arrows,
        'swan-carousel-variable-width': em.variableWidth,
        'swan-carousel-grid-gutters': N,
        'swan-carousel-grid-gutters-tight': N && 'tight' === A,
        ['swan-carousel-peek-non-focusable-' + eb]: !!C && eb % 2 == 0,
        [ei]: !et
    }), ev = {
        '--swan-internal-carousel-placeholder-slides-to-show-derived': eb || 1,
        '--swan-internal-carousel-placeholder-slides-to-show-xs': 'object' == typeof I ? I.xs : void 0,
        '--swan-internal-carousel-placeholder-slides-to-show-sm': 'object' == typeof I ? I.sm : void 0,
        '--swan-internal-carousel-placeholder-slides-to-show-md': 'object' == typeof I ? I.md : void 0,
        '--swan-internal-carousel-placeholder-slides-to-show-lg': 'object' == typeof I ? I.lg : void 0,
        '--swan-internal-carousel-placeholder-slides-to-show-xl': 'object' == typeof I ? I.xl : void 0
    };
    usePreventVerticalScroll(el);
    let [eg, ej] = useState(null), e_ = Children.toArray(E).length, eS = useMemo(()=>{
        let e = [];
        if (null === eg) return null;
        let r = eg + 1;
        for(let o = 0; o < eb; o++)e.push(r), r === e_ ? r = 1 : r++;
        return (null != q ? q : 1 === eb ? 'Slide {start} of {totalSlides}' : 'Slides {start} through {end} of {totalSlides}').replace('{start}', e[0].toString()).replace('{end}', e[e.length - 1].toString()).replace('{totalSlides}', e_.toString()).replace('{activeSlides}', e.join(', '));
    }, [
        eg,
        e_,
        eb,
        q
    ]), eO = useMemo(()=>Children.map(E, (e, r)=>isValidElement(e) && void 0 === e.props['aria-label'] ? cloneElement(e, {
                'aria-label': (null != H ? H : '{current} of {totalSlides}').replace('{current}', (r + 1).toString()).replace('{totalSlides}', e_.toString())
            }) : e), [
        H,
        E,
        e_
    ]);
    return useEffect(()=>{
        es(true);
    }, []), useEffect(()=>{
        'number' == typeof Q && en.current && en.current.slickGoTo(Q);
    }, [
        Q
    ]), (eb > e_ || 1 === e_ && 1 === eb) && (em.infinite = false), jsxs("div", {
        className: "swan-carousel-container",
        ref: el,
        "aria-label": z,
        "aria-labelledby": K,
        "aria-roledescription": R || 'carousel',
        role: $,
        style: ev,
        children: [
            et ? jsx(d, _object_spread_props(_object_spread(_object_spread_props(_object_spread({
                responsive: [
                    {
                        breakpoint: parseInt(tokensRaw.SwanBaseBreakpointXsEnd, 10),
                        settings: ef
                    }
                ]
            }, ew, em), {
                ref: ec,
                className: ey
            }), {
                children: eO,
                style: {
                    '--swan-internal-carousel-arrow-top': D,
                    '--swan-internal-carousel-arrow-vertical-offset': B
                }
            }), {
                beforeChange: (e, r)=>{
                    eu(e, r), ej(r);
                }
            })) : jsx(UninitializedCarousel, {
                className: ey,
                children: E
            }),
            jsx("div", {
                "data-testid": "carousel-aria-live",
                className: "swan-carousel-current-visible-slides",
                "aria-live": "polite",
                children: et ? eS : null
            })
        ]
    });
});

export { Carousel, UninitializedCarousel };
